import React, { useState, useEffect } from 'react';
import { Moon, Sun, X, LogOut, Users, Download, Info, Heart, HelpCircle } from 'lucide-react';
import { useLayout } from '../contexts/LayoutContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { fullVersion } from '../config/version';

const Menu = () => {
  const { isDarkMode, toggleDarkMode, isMenuOpen, closeMenu } = useLayout();
  const { signOut, currentUser } = useAuth();
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // If app is already installed, this event will fire
    window.addEventListener('appinstalled', () => {
      setIsInstallable(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
      setIsInstallable(false);
    }

    // Clear the deferredPrompt for the next time
    setDeferredPrompt(null);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (!currentUser) return 'Guest User';
    if (currentUser.isAnonymous) return 'Guest User';
    
    // For Google login, try to get the display name
    if (currentUser.providerData?.[0]?.providerId === 'google.com') {
      const displayName = currentUser.displayName;
      if (displayName) {
        const [firstName, ...lastNameParts] = displayName.split(' ');
        return `${firstName} ${lastNameParts.join(' ')}`;
      }
    }
    
    // For email/password users with profile
    if (currentUser.profile) {
      return `${currentUser.profile.firstName} ${currentUser.profile.lastName}`;
    }
    
    return currentUser.email || 'Guest User';
  };

  const isEditor = currentUser?.profile?.isEditor;

  return (
    <div
      className={`
        fixed inset-y-0 left-0 z-20 w-64
        bg-page dark:bg-page-dark
        transform transition-transform duration-300 ease-in-out
        ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        flex flex-col
        shadow-lg
      `}
    >
      <div className="p-4 flex justify-between items-center border-b border-default dark:border-dark">
        <h2 className="text-lg font-bold text-text-primary dark:text-text-primary-dark">
          Settings
        </h2>
        <button
          onClick={closeMenu}
          className="p-2 rounded-lg hover:bg-surface-hover dark:hover:bg-surface-hover-dark"
        >
          <X className="h-6 w-6 text-text-primary dark:text-text-primary-dark" />
        </button>
      </div>

      <div className="flex-1 p-4 space-y-4">
        <button
          onClick={toggleDarkMode}
          className="w-full p-3 rounded-lg flex items-center justify-between
            bg-surface dark:bg-surface-dark
            text-text-primary dark:text-text-primary-dark
            hover:bg-surface-hover dark:hover:bg-surface-hover-dark
            transition-colors"
        >
          <span className="font-medium">Dark Mode Toggle</span>
          {isDarkMode ? (
            <Moon className="h-5 w-5" />
          ) : (
            <Sun className="h-5 w-5" />
          )}
        </button>

        <button
          onClick={() => setShowHelp(true)}
          className="w-full p-3 rounded-lg flex items-center justify-between
            bg-surface dark:bg-surface-dark
            text-text-primary dark:text-text-primary-dark
            hover:bg-surface-hover dark:hover:bg-surface-hover-dark
            transition-colors"
        >
          <span className="font-medium">Help</span>
          <HelpCircle className="h-5 w-5" />
        </button>

        <button
          onClick={() => setShowAbout(true)}
          className="w-full p-3 rounded-lg flex items-center justify-between
            bg-surface dark:bg-surface-dark
            text-text-primary dark:text-text-primary-dark
            hover:bg-surface-hover dark:hover:bg-surface-hover-dark
            transition-colors"
        >
          <span className="font-medium">About</span>
          <Info className="h-5 w-5" />
        </button>

        {isInstallable && (
          <button
            onClick={handleInstall}
            className="w-full p-3 rounded-lg flex items-center justify-between
              bg-surface dark:bg-surface-dark
              text-text-primary dark:text-text-primary-dark
              hover:bg-surface-hover dark:hover:bg-surface-hover-dark
              transition-colors"
          >
            <span className="font-medium">Install App</span>
            <Download className="h-5 w-5" />
          </button>
        )}

        {isEditor && (
          <button
            onClick={() => navigate('/users')}
            className="w-full p-3 rounded-lg flex items-center justify-between
              bg-surface dark:bg-surface-dark
              text-text-primary dark:text-text-primary-dark
              hover:bg-surface-hover dark:hover:bg-surface-hover-dark
              transition-colors"
          >
            <span className="font-medium">Users</span>
            <Users className="h-5 w-5" />
          </button>
        )}
      </div>

      <div className="p-4 border-t border-default dark:border-default-dark">
        <p className="text-text-primary dark:text-text-primary-dark font-medium">
          {getUserDisplayName()}
        </p>
      </div>

      <div className="px-4">
        <div className="border-t border-default dark:border-dark" />
      </div>

      <div className="p-4">
        <button
          onClick={handleLogout}
          className="w-full p-3 rounded-lg flex items-center justify-between
            bg-status-error-bg dark:bg-status-error-bg-dark
            text-status-error-text dark:text-status-error-text-dark
            hover:bg-status-error-bg-hover dark:hover:bg-status-error-bg-hover-dark
            transition-colors"
        >
          <span className="font-medium">Sign Out</span>
          <LogOut className="h-5 w-5" />
        </button>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" style={{ width: '100vw', left: 0 }}>
          <div className="bg-surface dark:bg-surface-dark rounded-lg p-6 max-w-2xl mx-4 w-full max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center mb-4 flex-none">
              <h3 className="text-lg font-semibold text-text-primary dark:text-text-primary-dark">
                Help Guide
              </h3>
              <button
                onClick={() => setShowHelp(false)}
                className="p-2 rounded-lg hover:bg-surface-hover dark:hover:bg-surface-hover-dark"
              >
                <X className="h-5 w-5 text-text-primary dark:text-text-primary-dark" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 pr-2">
              <div className="space-y-6">
                <section>
                  <h4 className="text-lg font-semibold text-text-primary dark:text-text-primary-dark mb-2">
                    Airport Tags and Search
                  </h4>
                  <div className="space-y-2 text-text-primary dark:text-text-primary-dark">
                    <p><strong>Tags:</strong> Airports can be tagged with labels like "region1", "region2", "specialuse", "nightrestricted", etc. Tags help categorize airports and make them easier to find.</p>
                    <p><strong>Searching with Tags:</strong> You can search for airports by their tags in several ways:</p>
                    <ul className="list-disc list-inside pl-4 space-y-1">
                      <li>Type "region1" to filter airports tagged with "#region1"</li>
                      <li>Type partial tags like "#nigh" to find airports with tags like "#nightrestricted"</li>
                      <li>Combined searches like "#specialuse #region2" wil find airports with either #region2 or #specialuse in their tags</li>
                    </ul>
                    <p className="mt-2"><strong>Special Coloring:</strong> Airports tagged with "#specialuse" or "#nightrestricted" are highlighted with a different background color to make them easily identifiable in the list and on detail pages. This visual cue helps quickly identify airports with special operational considerations.</p>
                  </div>
                </section>

                <section>
                  <h4 className="text-lg font-semibold text-text-primary dark:text-text-primary-dark mb-2">
                    User Permissions
                  </h4>
                  <div className="space-y-2 text-text-primary dark:text-text-primary-dark">
                    <p><strong>Editor Role:</strong> Editors can add, edit, and delete airports, as well as manage user permissions and delete users. If you are an editor, please only use your powers for good.</p>
                    <p><strong>View Secret:</strong> Users with this permission can view sensitive operational and ground logistics.</p>
                    <p><strong>Regular Users:</strong> Can view public airport information and FBO services.</p>
                  </div>
                </section>

                <section>
                  <h4 className="text-lg font-semibold text-text-primary dark:text-text-primary-dark mb-2">
                    Managing Airports
                  </h4>
                  <div className="space-y-2 text-text-primary dark:text-text-primary-dark">
                    <p><strong>Adding New Airports:</strong></p>
                    <ol className="list-decimal list-inside pl-4 space-y-1">
                      <li>Click the "Add Airport" button on the main screen</li>
                      <li>Use the [Ai Fill] button to populate information</li>
                      <li>Verify information and [Save] the airport</li>
                      <li>Verify and edit operational considerations and services</li>
                    </ol>
                    <p className="mt-2"><strong>Editing Airports:</strong></p>
                    <ol className="list-decimal list-inside pl-4 space-y-1">
                      <li>Select an airport from the list</li>
                      <li>Click the edit button for the section you want to modify</li>
                      <li>Make your changes</li>
                      <li>Save the updated information</li>
                    </ol>
                  </div>
                </section>

                <section>
                  <h4 className="text-lg font-semibold text-text-primary dark:text-text-primary-dark mb-2">
                    Ai Fill Feature
                  </h4>
                  <div className="space-y-2 text-text-primary dark:text-text-primary-dark">
                    <p>The Ai Fill button searches the web for airport information and FBO listings. It populates the airport information and FBO listings automatically to save you time. It only works when first adding an airport.</p>
                    <p className="mt-2"><strong>Using AI Fill:</strong></p>
                    <ol className="list-decimal list-inside pl-4 space-y-1">
                      <li>Click the [Ai Fill] button when adding an airport</li>
                      <li>Review the generated content carefully</li>
                      <li>After you [Save] the airport, please review that additional information & services for accuracy</li>
                    </ol>
                    <p className="mt-2 text-status-error-text dark:text-status-error-text-dark">
                      ⚠️ Verify Ai generated content. ⚠️ 
                    </p>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* About Modal */}
      {showAbout && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" style={{ width: '100vw', left: 0 }}>
          <div className="bg-surface dark:bg-surface-dark rounded-lg p-6 max-w-md mx-4 w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-text-primary dark:text-text-primary-dark">
                About
              </h3>
              <button
                onClick={() => setShowAbout(false)}
                className="p-2 rounded-lg hover:bg-surface-hover dark:hover:bg-surface-hover-dark"
              >
                <X className="h-5 w-5 text-text-primary dark:text-text-primary-dark" />
              </button>
            </div>
            <div className="space-y-6 text-center">
              <div className="space-y-1">
                <h2 className="text-2xl font-bold text-text-primary dark:text-text-primary-dark">
                  Guardian Airport
                </h2>
                <h2 className="text-2xl font-bold text-text-primary dark:text-text-primary-dark">
                  Information
                </h2>
              </div>
              <div className="space-y-1">
                <p className="flex items-center justify-center gap-2 text-text-primary dark:text-text-primary-dark">
                  made with <Heart className="h-4 w-4 text-red-500 fill-current" /> by
                </p>
                <p className="text-text-primary dark:text-text-primary-dark">
                  the KAPA base
                </p>
              </div>
              <p className="text-sm text-text-secondary dark:text-text-secondary-dark">
                version {fullVersion}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Menu; 